import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TimetableSlotFields } from './TimetableSlotFields';

// Icons
import { 
  Calendar,
  Clock,
  Plus,
  Edit,
  Trash2,
  User,
  BookOpen,
  GraduationCap,
  Search,
  Filter,
  AlertTriangle,
  MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimetableManagementAdvanced = ({ user }) => {
  const [timetables, setTimetables] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedDay, setSelectedDay] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTimetable, setSelectedTimetable] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [timetableForm, setTimetableForm] = useState({
    class_id: '',
    subject_id: '',
    teacher_id: '',
    day_of_week: 0,
    start_time: '',
    end_time: '',
    room: ''
  });

  const daysOfWeek = [
    { value: 0, label: 'Lundi', short: 'Lun' },
    { value: 1, label: 'Mardi', short: 'Mar' },
    { value: 2, label: 'Mercredi', short: 'Mer' },
    { value: 3, label: 'Jeudi', short: 'Jeu' },
    { value: 4, label: 'Vendredi', short: 'Ven' },
    { value: 5, label: 'Samedi', short: 'Sam' },
    { value: 6, label: 'Dimanche', short: 'Dim' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [timetablesResponse, classesResponse, subjectsResponse, teachersResponse] = await Promise.all([
        axios.get(`${API}/timetables`),
        axios.get(`${API}/classes`),
        axios.get(`${API}/subjects`),
        axios.get(`${API}/users?role=TEACHER`)
      ]);

      setTimetables(timetablesResponse.data);
      setClasses(classesResponse.data);
      setSubjects(subjectsResponse.data);
      setTeachers(teachersResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshTimetables = async () => {
    const response = await axios.get(`${API}/timetables`, { params: { _t: Date.now() } });
    setTimetables(response.data);
  };

  const handleCreateTimetable = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API}/timetables`, timetableForm);
      setTimetables(prev => [...prev.filter(t => t.id !== response.data.id), response.data]);
      refreshTimetables().catch(() => {});
      toast.success('Créneau ajouté avec succès');
      resetForm();
      setCreateDialogOpen(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(errorMsg);
      console.error('Error creating timetable:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTimetable = async (e) => {
    e.preventDefault();
    if (!selectedTimetable || saving) return;
    setSaving(true);
    try {
      const response = await axios.put(`${API}/timetables/${selectedTimetable.id}`, timetableForm);
      setTimetables(prev => prev.map(t => 
        t.id === selectedTimetable.id ? response.data : t
      ));
      refreshTimetables().catch(() => {});
      toast.success('Créneau modifié avec succès');
      resetForm();
      setEditDialogOpen(false);
      setSelectedTimetable(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la modification';
      toast.error(errorMsg);
      console.error('Error updating timetable:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTimetable = async (timetableId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce créneau ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/timetables/${timetableId}`);
      setTimetables(prev => prev.filter(t => t.id !== timetableId));
      toast.success('Créneau supprimé avec succès');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(errorMsg);
      console.error('Error deleting timetable:', error);
    }
  };

  const resetForm = () => {
    setTimetableForm({
      class_id: '',
      subject_id: '',
      teacher_id: '',
      day_of_week: 0,
      start_time: '',
      end_time: '',
      room: ''
    });
  };

  const openEditDialog = (timetable) => {
    setSelectedTimetable(timetable);
    setTimetableForm({
      class_id: timetable.class_id,
      subject_id: timetable.subject_id,
      teacher_id: timetable.teacher_id,
      day_of_week: timetable.day_of_week,
      start_time: timetable.start_time,
      end_time: timetable.end_time,
      room: timetable.room || ''
    });
    setEditDialogOpen(true);
  };

  const getClassName = (classId) => {
    const schoolClass = classes.find(c => c.id === classId);
    return schoolClass ? schoolClass.name : 'Classe inconnue';
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'Matière inconnue';
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? `${teacher.first_name} ${teacher.last_name}` : 'Enseignant inconnu';
  };

  const getDayLabel = (dayNumber) => {
    const day = daysOfWeek.find(d => d.value === dayNumber);
    return day ? day.label : 'Jour inconnu';
  };

  const filteredTimetables = timetables.filter(timetable => {
    const classMatch = !selectedClass || selectedClass === 'all' || timetable.class_id === selectedClass;
    const dayMatch = selectedDay === '' || selectedDay === 'all' || timetable.day_of_week === parseInt(selectedDay);
    return classMatch && dayMatch;
  });

  // Grouper les emplois du temps par jour et heure pour l'affichage en grille
  const createWeeklyGrid = () => {
    const grid = {};
    
    daysOfWeek.forEach(day => {
      grid[day.value] = [];
    });

    filteredTimetables.forEach(timetable => {
      if (!grid[timetable.day_of_week]) {
        grid[timetable.day_of_week] = [];
      }
      grid[timetable.day_of_week].push(timetable);
    });

    // Trier par heure de début
    Object.keys(grid).forEach(day => {
      grid[day].sort((a, b) => a.start_time.localeCompare(b.start_time));
    });

    return grid;
  };

  const weeklyGrid = createWeeklyGrid();

  if (user.role !== 'ADMIN' && user.role !== 'PRINCIPAL') {
    return (
      <div className="p-6">
        <Card className="card">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 mx-auto text-red-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Accès refusé</h3>
              <p className="text-gray-600">
                Vous n'avez pas les permissions nécessaires pour gérer les emplois du temps.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <Calendar className="w-6 h-6 mr-2 text-blue-600" />
            Gestion des Emplois du Temps
          </h1>
          <p className="text-gray-600 mt-1">
            Organisez les créneaux par matière, classe et enseignant
          </p>
        </div>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className={`btn-primary ${user.role === 'ADMIN' ? '' : 'hidden'}`} data-testid="new-slot-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau créneau
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter un créneau</DialogTitle>
              <DialogDescription>
                Créez un nouveau créneau dans l'emploi du temps
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTimetable} className="space-y-4" data-testid="create-slot-form">
              <TimetableSlotFields form={timetableForm} setForm={setTimetableForm} classes={classes} subjects={subjects} teachers={teachers} prefix="create-slot" />

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => {
                  resetForm();
                  setCreateDialogOpen(false);
                }}>
                  Annuler
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="create-slot-submit">
                  Créer le créneau
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-blue-600" />
              Total Créneaux
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{timetables.length}</div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <GraduationCap className="w-4 h-4 mr-2 text-green-600" />
              Classes Concernées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {new Set(timetables.map(t => t.class_id)).size}
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <BookOpen className="w-4 h-4 mr-2 text-purple-600" />
              Matières Enseignées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {new Set(timetables.map(t => t.subject_id)).size}
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <User className="w-4 h-4 mr-2 text-orange-600" />
              Enseignants Actifs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {new Set(timetables.map(t => t.teacher_id)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <Select value={selectedClass} onValueChange={setSelectedClass}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Filtrer par classe" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les classes</SelectItem>
            {classes.map((schoolClass) => (
              <SelectItem key={schoolClass.id} value={schoolClass.id}>
                {schoolClass.name} - {schoolClass.level}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedDay} onValueChange={setSelectedDay}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrer par jour" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les jours</SelectItem>
            {daysOfWeek.map((day) => (
              <SelectItem key={day.value} value={day.value.toString()}>
                {day.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Weekly Grid View */}
      <Tabs defaultValue="grid" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="grid">Vue Grille</TabsTrigger>
          <TabsTrigger value="list">Vue Liste</TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="space-y-4">
          <div className="grid grid-cols-7 gap-2">
            {daysOfWeek.map((day) => (
              <div key={day.value}>
                <div className="bg-gray-100 p-3 text-center font-medium text-gray-700 rounded-t-lg">
                  {day.label}
                </div>
                <div className="border border-t-0 min-h-48 p-2 rounded-b-lg">
                  {weeklyGrid[day.value].map((timetable) => (
                    <div
                      key={timetable.id}
                      className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs"
                    >
                      <div className="font-medium text-blue-800">
                        {timetable.start_time} - {timetable.end_time}
                      </div>
                      <div className="text-blue-700">
                        {getSubjectName(timetable.subject_id)}
                      </div>
                      <div className="text-blue-600">
                        {getClassName(timetable.class_id)}
                      </div>
                      <div className="text-blue-600 text-xs">
                        {getTeacherName(timetable.teacher_id)}
                      </div>
                      {timetable.room && (
                        <div className="text-blue-500 text-xs flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />
                          {timetable.room}
                        </div>
                      )}
                      <div className={`flex space-x-1 mt-1 ${user.role === 'ADMIN' ? '' : 'hidden'}`}>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-slot-${timetable.id}`}
                          onClick={() => openEditDialog(timetable)}
                          className="h-6 text-xs"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteTimetable(timetable.id)}
                          className="h-6 text-xs text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="list" className="space-y-4">
          {filteredTimetables.length > 0 ? (
            <div className="space-y-3">
              {filteredTimetables.map((timetable) => (
                <Card key={timetable.id} className="card">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Clock className="w-4 h-4 text-gray-600" />
                          <span className="font-semibold">
                            {getDayLabel(timetable.day_of_week)} • {timetable.start_time} - {timetable.end_time}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="font-medium text-gray-700">Matière</p>
                            <p className="text-blue-600">{getSubjectName(timetable.subject_id)}</p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-700">Classe</p>
                            <p className="text-green-600">{getClassName(timetable.class_id)}</p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-700">Enseignant</p>
                            <p className="text-purple-600">{getTeacherName(timetable.teacher_id)}</p>
                          </div>
                        </div>

                        {timetable.room && (
                          <div className="mt-2 flex items-center text-sm text-gray-600">
                            <MapPin className="w-4 h-4 mr-1" />
                            Salle: {timetable.room}
                          </div>
                        )}
                      </div>

                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(timetable)}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteTimetable(timetable.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun créneau trouvé</h3>
              <p className="text-gray-600 mb-4">
                {selectedClass || selectedDay ? 'Aucun créneau ne correspond aux filtres.' : 'Aucun créneau créé.'}
              </p>
              <Button onClick={() => setCreateDialogOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Créer le premier créneau
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le créneau</DialogTitle>
            <DialogDescription>
              Modifiez les informations du créneau
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateTimetable} className="space-y-4" data-testid="edit-slot-form">
            <TimetableSlotFields form={timetableForm} setForm={setTimetableForm} classes={classes} subjects={subjects} teachers={teachers} prefix="edit-slot" />

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                resetForm();
                setEditDialogOpen(false);
                setSelectedTimetable(null);
              }}>
                Annuler
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="edit-slot-submit">
                Mettre à jour
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimetableManagementAdvanced;